import { z } from "zod";
import { COURSE_IDS } from "./courses.js";
import { EMAIL_STATUSES, PROSPECT_STATUSES } from "./enums.js";
/**
 * The contact-form contract - the single source of truth for the React form,
 * the submit Lambda, and the DynamoDB item shape (T3 in the eng review).
 *
 * `inquiryInputSchema` validates what the public form POSTs. Server-derived
 * fields (id, status, timestamps, emailStatus) are added in `prospectSchema`.
 */
const phone = z
    .string()
    .trim()
    .max(25)
    .regex(/^[0-9 ()+\-.]+$/, "Enter a valid phone number")
    // Require a real number, not just formatting characters: 10-15 digits
    // (10 = US local, up to 15 = E.164 with country code).
    .refine((v) => {
    const digits = v.replace(/\D/g, "").length;
    return digits >= 10 && digits <= 15;
}, "Enter a valid phone number (at least 10 digits)");
export const inquiryInputSchema = z.object({
    fullName: z.string().trim().min(1, "Please enter your name").max(120),
    email: z.string().trim().toLowerCase().email("Enter a valid email").max(160),
    phone,
    mailingAddress: z.string().trim().max(300).optional().default(""),
    courseId: z.enum(COURSE_IDS, { errorMap: () => ({ message: "Choose a program" }) }),
    preferredBatchMonth: z.string().trim().min(1, "Choose a start month").max(40),
    comments: z.string().trim().max(2000).optional().default(""),
    howHeard: z.string().trim().max(200).optional().default(""),
    /**
     * Honeypot: a hidden field real users never fill. Bots do. If non-empty the
     * submit Lambda silently drops the request (no lead, no email). Cheap,
     * AWS-only first line of spam defense alongside the CloudFront WAF rules.
     */
    website: z.string().max(0).optional().default(""),
});
export const prospectSchema = inquiryInputSchema
    .omit({ website: true })
    .extend({
    prospectId: z.string().uuid(),
    status: z.enum(PROSPECT_STATUSES).default("New"),
    emailStatus: z.enum(EMAIL_STATUSES).default("pending"),
    source: z.string().default("web-form"),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /**
     * Lead-nurture progress: the last automated-reminder milestone (days since
     * inquiry) already sent - 0/absent = none yet, then 7, 14, 28. Drives the
     * daily reminder sweep's idempotency. `lastReminderAt` is the timestamp of
     * the most recent automated nudge.
     */
    nurtureStage: z.number().int().nonnegative().optional(),
    lastReminderAt: z.string().datetime().optional(),
    /**
     * Email opt-out: set true when the prospect clicks the unsubscribe link in a
     * follow-up email. The nurture sweep skips opted-out leads. `unsubToken` is a
     * random per-lead secret embedded in the unsubscribe URL so only the actual
     * recipient (holding the link) can opt out - not anyone guessing a prospectId.
     */
    emailOptOut: z.boolean().optional(),
    unsubToken: z.string().optional(),
});
//# sourceMappingURL=prospect.js.map